/* eslint-disable prettier/prettier */
import React, { FC, useCallback, useMemo } from 'react';
import { useExternalScript, WindowWidthContext } from '@minecraft.utils';
import { BasicContainer, Loader } from '@minecraft.atoms';
import { QUERY_PARAMS } from '@blocs.auth';
import Modal from '@minecraft.modal';
import { Profile } from '@minecraft.graphql-operations';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import { getEnvironment } from '@minecraft.environment';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { PaywallLocation } from './enums/PaywallLocation';
import { useBillingPopup } from './BillingPopupContext';
import { usePaywall } from './usePaywall';
import { PAYWALL_STEPS } from './enums/paywall';
import { usePaywallRenewal } from './usePaywallRenewal';
import { BillingCodes, MediaPurchase, TemperatureValues } from './enums/plans';
import { usePurchaseMedia } from './usePurchaseMedia';
import { Purchase } from './types';
import { UpgradeModalExperimentGroups } from './enums/experimentGroups';
import { ThreeDSecureForm, useThreeDSecureFlow } from './ThreeDSecureFlow';
import { useTranslation } from '../I18N';
import { DefaultPaywallExperience, SingleClickReactivation } from './PaywallVariants';
import { PaywallModalTypes } from './usePaywallModalTypes';
import { usePaywallFunnelEvents } from './usePaywallFunnelEvents';

export interface PaywallModalProps {
  temperature?: TemperatureValues | '';
}

export interface PaywallWrapperProps {
  isModal?: boolean;
  startingStep?: PAYWALL_STEPS;
  paywallLocation?: PaywallLocation;
  onPaymentCompleteRedirect?: (_?: string) => void;
  mediaIds?: number[] | null;
  currentProfile?: Profile;
}

interface PaywallWrapperWithModalProps {
  isModal: boolean;
  title: string | React.ReactNode;
  visible: boolean;
  children?: React.ReactNode;
  step?: PAYWALL_STEPS;
  onModalClose?: () => void;
  modalVariant?: UpgradeModalExperimentGroups;
  isSingleClickReactivation?: boolean;
}

const PaywallWrapperWithModal: FC<PaywallWrapperWithModalProps> = ({
  isModal,
  title,
  visible,
  children,
  step,
  onModalClose,
  modalVariant,
  isSingleClickReactivation,
}) => {
  const { location } = useBillingPopup();
  const isMobile = WindowWidthContext.useIsMobile();

  const isSuccess = step === PAYWALL_STEPS.SUCCESS;
  const isPlanSelection = step === PAYWALL_STEPS.INITIAL;

  const isLifestyleUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantB;
  const isSinglePageUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantA;
  const isSuccessOrSinglePageUpgradeModal = isSuccess || isSinglePageUpgradeModal;
  const isPlanSelectionWithLifestyle = isPlanSelection && isLifestyleUpgradeModal;
  const isSuccessOrInitialWithSingleClickReactivation = (isSuccess || isPlanSelection) && isSingleClickReactivation;

  const noPadding =
    isSuccessOrSinglePageUpgradeModal || isPlanSelectionWithLifestyle || isSuccessOrInitialWithSingleClickReactivation;
  const closeIconInverted =
    isSuccessOrSinglePageUpgradeModal || isPlanSelectionWithLifestyle || isSuccessOrInitialWithSingleClickReactivation;
  const isHeaderHidden =
    isSuccessOrSinglePageUpgradeModal ||
    isPlanSelectionWithLifestyle ||
    !isMobile ||
    isSuccessOrInitialWithSingleClickReactivation;
  const noBodyPadding = isMobile || isSuccessOrSinglePageUpgradeModal || isSuccessOrInitialWithSingleClickReactivation;
  const modalSize = useMemo(() => {
    if (isSuccessOrInitialWithSingleClickReactivation) return 'small';
    if (isSinglePageUpgradeModal) return 'large';
    return 'xlarge';
  }, [isSuccessOrInitialWithSingleClickReactivation, isSinglePageUpgradeModal]);

  if (isModal) {
    return (
      <Modal
        data-testid="next-billing-popup-modal-wrapper"
        title={title}
        isOpen={visible}
        handleToggleClick={onModalClose}
        modalSize={modalSize}
        height={isMobile ? 'auto' : 'full'}
        bodyProps={{ className: noBodyPadding ? 'cn_atom_p-0' : 'cn_atom_pt-8' }}
        customHeaderPadding={{ top: 1, bottom: 1, left: 0, right: 0 }}
        closeIconColor={closeIconInverted ? 'inverted' : undefined}
        isHeaderHidden={isHeaderHidden}
        noPadding={noPadding}
        isFooterHidden
      >
        {children}
      </Modal>
    );
  }

  return (
    <BasicContainer
      w="full"
      m="auto"
      className={`${location !== PaywallLocation.MEMBERSHIP && !isSuccess ? 'cn_max-w-lg' : ''}`}
      data-testid="next-paywall-wrapper"
    >
      {children}
    </BasicContainer>
  );
};

export const PaywallWrapper: React.FC<PaywallWrapperProps> = React.memo(
  ({
    isModal = false,
    startingStep = PAYWALL_STEPS.INITIAL,
    onPaymentCompleteRedirect = () => null,
    mediaIds: mediaIdFromProps,
    currentProfile,
  }) => {
    const { t } = useTranslation();

    const config = getEnvironment();
    const billingPlatformPublicKey = config['RECURLY_PUBLIC_KEY'];
    const [externalScriptLoaded, loadBillingScriptError] = useExternalScript('https://js.recurly.com/v4/recurly.js');

    const {
      visible,
      location,
      mediaIds: mediaIdsFromContext,
      show: showBillingPopup,
      close: closeBillingPopup,
      subscription,
      previousSubscription,
      countryCode,
      plans,
      isTrialEligible,
      selectedPaymentOption,
      setCompletedPurchase,
      setCompletePurchaseError,
      isAnnualRenewalDue,
      completedPurchase,
      completePurchaseLoading,
      currencyCode: subscriptionCurrencyCode,
      currentPurchase,
      addPaymentError,
      addPaymentLoading,
      applyCouponError,
      completePurchaseError,
      couponLoading,
      onAddPaymentOption,
      applyCoupon,
      removeCoupon,
      setSelectedPaymentOption,
      plansError,
      plansLoading,
      discountType,
      setShowAddPaymentMethod,
      showAddPaymentMethod,
      wallet,
      walletLoading,
      isWithinTrialPeriod,
      completePurchase,
      isReactivate,
      selectedPlan,
      setSelectedPlan,
      currentPlan,
      onUpdatePaymentOption,
      modalVariant,
      modalType,
      setVisibleError,
    } = useBillingPopup();

    const mediaIds = mediaIdFromProps || mediaIdsFromContext;
    const isDeeplink = localStorage.getItem(QUERY_PARAMS.deeplink as string)?.toLocaleLowerCase() === 'true';
    const isMobile = WindowWidthContext.useIsMobile();
    const startPaywallStep = isDeeplink ? PAYWALL_STEPS.CHECKOUT : startingStep;
    const hideIsAnnualToggle = !plans?.length && !isModal;
    const isAnnualToggleLocked = plans?.length === 1;
    const isSingleClickReactivation = modalType === PaywallModalTypes.SINGLE_CLICK_REACTIVATION;

    const {
      step,
      getTitle,
      onNext,
      onBack,
      resetStep,
      skipToLastStep,
      moveToCheckoutStep,
      groupedPlans,
      setIsAnnual,
      isAnnual,
    } = usePaywall({
      startingStep: startPaywallStep,
      selectedPlan,
      setSelectedPlan,
      showBillingPopup,
      closeBillingPopup,
      paywallLocation: isModal && !visible ? undefined : location,
      plans,
      mediaIds,
      isModal,
      currentPlan,
      modalVariant,
      previousSubscriptionPlan: previousSubscription?.plan?.billingCode,
      discountType,
    });

    const {
      previewMediaPurchase,
      previewMediaPurchaseLoading,
      previewMediaPurchaseError,
      completePurchaseMedia,
      applyPurchaseMediaCoupon,
      removePurchaseMediaCoupon,
      applyPurchaseMediaCouponError,
      purchaseMediaCouponLoading,
      currencyCode: mediaPurchaseCurrencyCode,
    } = usePurchaseMedia({
      selectedPlan,
      selectedPaymentOption,
      paywallLocation: location,
      setCompletedPurchase,
      setCompletePurchaseError,
      mediaIds,
      onNext,
    });

    const { currentRenewalPurchase, completeRenewalPurchase, renewalCurrencyCode, enableAnnualRenewal } =
      usePaywallRenewal({
        selectedPlan,
        selectedPaymentOption,
        paywallLocation: location,
        setCompletedPurchase,
        setCompletePurchaseError,
        isAnnualRenewalDue,
        isAnnual,
        onNext,
      });

    const {
      threeDActionContext,
      onAddPaymentOptionWith3DS,
      onUpdatePaymentOptionWith3DS,
      onCompletePurchaseWith3DS,
      onThreeDActionToken,
      onThreeDActionError,
    } = useThreeDSecureFlow({
      isSingleClickReactivation,
      onAddPaymentOption,
      onUpdatePaymentOption,
      onCompletePurchase: completePurchase,
      setVisibleError,
      moveToCheckoutStep,
      skipToLastStep,
      onNext,
    });

    const isMediaPurchase = selectedPlan?.billingCode === MediaPurchase.alacarte;
    const purchasePreview = (isMediaPurchase ? previewMediaPurchase : currentPurchase) as Purchase;
    const completePurchaseMutation = isMediaPurchase ? completePurchaseMedia : onCompletePurchaseWith3DS;
    const onApplyCoupon = isMediaPurchase ? applyPurchaseMediaCoupon : applyCoupon;
    const applyCouponErr = isMediaPurchase ? applyPurchaseMediaCouponError : applyCouponError;
    const onRemoveCoupon = isMediaPurchase ? removePurchaseMediaCoupon : removeCoupon;
    const isCouponLoading = isMediaPurchase ? purchaseMediaCouponLoading : couponLoading;
    const currencyCode = isMediaPurchase ? mediaPurchaseCurrencyCode : subscriptionCurrencyCode;

    const upgradeFunnelEvents = usePaywallFunnelEvents(location, isModal);
    const onModalClose = useCallback(() => {
      if (isSingleClickReactivation) {
        captureHeapEvent({ name: HEAP_EVENT_NAMES.REACTIVATION_MODAL_DISMISS });
      } else if (!completePurchaseError && !completedPurchase) {
        upgradeFunnelEvents.trackCheckoutAbandoned();
      }
      closeBillingPopup();
      resetStep();
    }, [
      closeBillingPopup,
      isSingleClickReactivation,
      resetStep,
      completePurchaseError,
      completedPurchase,
      upgradeFunnelEvents,
    ]);

    const onCompleteRedirect = (url?: string) => {
      onPaymentCompleteRedirect(url);
      resetStep();
    };

    return (
      <PaywallWrapperWithModal
        isModal={isModal}
        title={getTitle({ paywallLocation: location, isHidden: !(isMobile && isModal), isReactivate })}
        visible={visible}
        step={step}
        onModalClose={onModalClose}
        modalVariant={modalVariant}
        isSingleClickReactivation={isSingleClickReactivation || completedPurchase?.isSingleClickReactivation}
      >
        <Loader
          data-testid="paywall-recurly-script-loader"
          loading={(!externalScriptLoaded && !loadBillingScriptError) || !window.recurly}
          error={loadBillingScriptError && t('common:globalErrors.generic')}
        >
          <RecurlyProvider publicKey={billingPlatformPublicKey} required={['cvv', 'postal_code']}>
            <Elements>
              {(isSingleClickReactivation || completedPurchase?.isSingleClickReactivation) && isModal ? (
                <SingleClickReactivation
                  step={step}
                  addPaymentError={addPaymentError}
                  addPaymentLoading={addPaymentLoading}
                  applyCouponError={applyCouponErr}
                  completePurchaseError={completePurchaseError}
                  completedPurchase={completedPurchase}
                  completePurchaseLoading={completePurchaseLoading}
                  countryCode={countryCode}
                  couponLoading={isCouponLoading}
                  currencyCode={enableAnnualRenewal ? renewalCurrencyCode : currencyCode}
                  currentPlanCode={subscription?.plan?.billingCode as BillingCodes}
                  currentPurchase={enableAnnualRenewal ? currentRenewalPurchase : purchasePreview}
                  enableAnnualRenewal={enableAnnualRenewal}
                  getTitle={getTitle}
                  isAnnual={isAnnual}
                  isModal={isModal}
                  lessMd={isMobile}
                  onAddPaymentOption={onAddPaymentOptionWith3DS}
                  onApplyCoupon={onApplyCoupon}
                  onBack={onBack}
                  onCompletePurchase={enableAnnualRenewal ? completeRenewalPurchase : completePurchaseMutation}
                  onNext={onNext}
                  onRemoveCoupon={onRemoveCoupon}
                  onSelectPaymentOption={setSelectedPaymentOption}
                  onThreeDActionError={onThreeDActionError}
                  paywallLocation={location}
                  plans={groupedPlans}
                  plansError={plansError}
                  plansLoading={plansLoading}
                  selectedPaymentOption={selectedPaymentOption}
                  selectedPlan={selectedPlan}
                  selectedPlanTerm={isAnnual ? 'annual' : 'monthly'}
                  setIsAnnual={setIsAnnual}
                  setSelectedPlan={setSelectedPlan}
                  setShowAddPaymentMethod={setShowAddPaymentMethod}
                  showAddPaymentMethod={showAddPaymentMethod}
                  onUpdatePaymentOption={onUpdatePaymentOptionWith3DS}
                  startingStep={startPaywallStep}
                  threeDActionContext={threeDActionContext}
                  wallet={wallet}
                  walletLoading={walletLoading}
                  isWithinTrialPeriod={isWithinTrialPeriod}
                  previewMediaPurchase={previewMediaPurchase}
                  previewMediaPurchaseLoading={previewMediaPurchaseLoading}
                  previewMediaPurchaseError={previewMediaPurchaseError}
                  closeBillingPopup={onModalClose}
                  onPaymentCompleteRedirect={onCompleteRedirect}
                  isAnnualToggleLocked={isAnnualToggleLocked}
                  currentProfile={currentProfile}
                  modalVariant={modalVariant}
                />
              ) : (
                <DefaultPaywallExperience
                  step={step}
                  isModal={isModal}
                  selectedPlan={selectedPlan}
                  completedPurchase={completedPurchase}
                  lessMd={isMobile}
                  completePurchaseLoading={completePurchaseLoading}
                  paywallLocation={location}
                  onPaymentCompleteRedirect={onCompleteRedirect}
                  subscription={subscription}
                  countryCode={countryCode}
                  closeBillingPopup={onModalClose}
                  addPaymentError={addPaymentError}
                  addPaymentLoading={addPaymentLoading}
                  applyCouponError={applyCouponErr}
                  completePurchaseError={completePurchaseError}
                  couponLoading={isCouponLoading}
                  currencyCode={enableAnnualRenewal ? renewalCurrencyCode : currencyCode}
                  currentPlanCode={subscription?.plan?.billingCode as BillingCodes}
                  currentPurchase={enableAnnualRenewal ? currentRenewalPurchase : purchasePreview}
                  enableAnnualRenewal={enableAnnualRenewal}
                  getTitle={getTitle}
                  isAnnual={isAnnual}
                  onAddPaymentOption={onAddPaymentOptionWith3DS}
                  onApplyCoupon={onApplyCoupon}
                  onBack={onBack}
                  onCompletePurchase={enableAnnualRenewal ? completeRenewalPurchase : completePurchaseMutation}
                  onNext={onNext}
                  onRemoveCoupon={onRemoveCoupon}
                  onSelectPaymentOption={setSelectedPaymentOption}
                  onThreeDActionError={onThreeDActionError}
                  plans={groupedPlans}
                  plansError={plansError}
                  plansLoading={plansLoading}
                  selectedPaymentOption={selectedPaymentOption}
                  selectedPlanTerm={isAnnual ? 'annual' : 'monthly'}
                  setIsAnnual={setIsAnnual}
                  setSelectedPlan={setSelectedPlan}
                  setShowAddPaymentMethod={setShowAddPaymentMethod}
                  showAddPaymentMethod={showAddPaymentMethod}
                  onUpdatePaymentOption={onUpdatePaymentOptionWith3DS}
                  startingStep={startPaywallStep}
                  threeDActionContext={threeDActionContext}
                  wallet={wallet}
                  walletLoading={walletLoading}
                  isWithinTrialPeriod={isWithinTrialPeriod}
                  previewMediaPurchase={previewMediaPurchase}
                  previewMediaPurchaseLoading={previewMediaPurchaseLoading}
                  previewMediaPurchaseError={previewMediaPurchaseError}
                  isAnnualToggleLocked={isAnnualToggleLocked}
                  currentProfile={currentProfile}
                  modalVariant={modalVariant}
                  isTrialEligible={isTrialEligible}
                  isReactivate={isReactivate}
                  hideIsAnnualToggle={hideIsAnnualToggle}
                  discountType={discountType}
                />
              )}
              {threeDActionContext?.token && (
                <ThreeDSecureForm
                  threeDActionToken={threeDActionContext.token}
                  onToken={onThreeDActionToken}
                  onError={onThreeDActionError}
                  loading={addPaymentLoading}
                />
              )}
            </Elements>
          </RecurlyProvider>
        </Loader>
      </PaywallWrapperWithModal>
    );
  }
);
